import { useCallback } from "react";

import { useShopcluesCategorySchemaDigestMutation } from "@/graphql/generated";

import { asRecord, slugify } from "./shopcluesSchemaDigest";
import type {
  ShopcluesFieldCard,
  ShopcluesIngestResult,
} from "./useShopcluesSchemaUpload";

/**
 * Fetches a ShopClues category schema live over the ShopClues Feature APIs and
 * ingests the resulting field cards into the canonical AI taxonomy pipeline.
 * Same output shape as {@link useShopcluesSchemaUpload} so the preview UI is
 * shared; the input is a category id instead of an Excel file.
 */
export function useShopcluesCategoryFetch() {
  const { mutateAsync, isPending } = useShopcluesCategorySchemaDigestMutation();

  const fetchSchema = useCallback(
    async (params: {
      categoryId: string;
      productType: string;
      canonicalTaxonId?: string;
    }): Promise<ShopcluesIngestResult> => {
      const canonicalTaxonId =
        params.canonicalTaxonId?.trim() ||
        `shopclues.${slugify(params.productType)}`;

      const res = await mutateAsync({
        input: {
          categoryId: params.categoryId.trim(),
          productType: params.productType.trim(),
          canonicalTaxonId,
        },
      });

      const digest = res.shopcluesCategorySchemaDigest;
      const payload = asRecord(digest.canonicalSchemaPayload);
      const fieldCards = Array.isArray(payload?.field_cards)
        ? (payload!.field_cards as ShopcluesFieldCard[])
        : [];
      const schemaVersion =
        typeof payload?.schema_version === "string"
          ? (payload.schema_version as string)
          : null;
      const ai = asRecord(digest.aiResponse);
      const mappingSessionId =
        typeof ai?.mapping_session_id === "string"
          ? (ai.mapping_session_id as string)
          : null;

      return {
        marketplaceId: digest.marketplaceId,
        productType: digest.productType,
        fieldCards,
        schemaVersion,
        mappingSessionId,
        aiResponse: digest.aiResponse,
      };
    },
    [mutateAsync],
  );

  return { fetchSchema, isPending };
}
